import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useData } from "@/context/DataContext";
import MenuItemCard from "@/components/MenuItemCard";
import ItemDialog from "@/components/ItemDialog";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function MenuPage() {
  const { menu, loading, reload } = useData();
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const [dialogItem, setDialogItem] = useState(null);
  const [params, setParams] = useSearchParams();
  const tabsRef = useRef(null);

  useEffect(() => {
    const id = params.get("item");
    const it = id && menu.items.find((i) => i.item_id === id);
    if (!it) return;
    setActive(it.category_id);
    setDialogItem(it);
    setParams({}, { replace: true });
  }, [params, menu, setParams]);

  useEffect(() => {
    const c = tabsRef.current;
    const el = c?.querySelector('[data-active="true"]');
    if (c && el) c.scrollTo({ left: el.offsetLeft - c.clientWidth / 2 + el.clientWidth / 2, behavior: "smooth" });
  }, [active, query]);

  useEffect(() => {
    reload();
    const onFocus = () => reload();
    window.addEventListener("focus", onFocus);
    const onVisible = () => { if (!document.hidden) reload(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reload]);

  useEffect(() => {
    if (!active && menu.categories.length) setActive(menu.categories[0].category_id);
  }, [menu, active]);

  const itemsByCat = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) return menu.items.filter((i) => i.name.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q));
    return menu.items.filter((i) => i.category_id === active);
  }, [menu, active, query]);

  return (
    <div>
      <div className="relative py-16 bg-gradient-to-b from-emerald-950 to-[#101311] border-b border-emerald-900/50 tropical-grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400">Order Online · Pickup & Delivery</span>
          <h1 className="font-display font-black uppercase tracking-tight text-5xl sm:text-6xl mt-2">The Menu</h1>
          <p className="text-[#FDFBF7]/70 mt-3">Bold Jamaican plates, made to order. Prices are set by Sir. King Kitchen.</p>
          <div className="max-w-md mx-auto mt-6 relative">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#FDFBF7]/40" />
            <input data-testid="menu-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search dishes…" className="w-full bg-[#181D1A] rounded-full pl-12 pr-4 py-3.5 border border-emerald-800/50 outline-none focus:border-amber-400" />
          </div>
        </div>
      </div>

      <div className="sticky top-16 md:top-20 z-30 bg-[#101311]/95 backdrop-blur-md border-b border-emerald-900/50">
        <div className="relative max-w-7xl mx-auto">
          <div ref={tabsRef} className="flex gap-2 overflow-x-auto no-scrollbar momentum-scroll px-3 sm:px-8 py-3">
            {menu.categories.map((c) => (
              <button
                key={c.category_id}
                data-testid={`menu-category-tab-${slug(c.name)}`}
                data-active={active === c.category_id && !query}
                onClick={() => { setActive(c.category_id); setQuery(""); }}
                className={`whitespace-nowrap px-5 py-2.5 rounded-full font-display font-bold uppercase tracking-wide text-sm border transition-colors ${
                  active === c.category_id && !query ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-800/50 text-[#FDFBF7]/80 hover:border-amber-400"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-[#101311] to-transparent sm:hidden" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        {loading ? (
          <p className="text-center text-[#FDFBF7]/50 py-20">Loading menu…</p>
        ) : itemsByCat.length === 0 ? (
          <p className="text-center text-[#FDFBF7]/50 py-20">No dishes found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {itemsByCat.map((item) => (
              <MenuItemCard key={item.item_id} item={item} onSelect={setDialogItem} />
            ))}
          </div>
        )}
      </div>

      <ItemDialog item={dialogItem} open={!!dialogItem} onClose={() => setDialogItem(null)} />
    </div>
  );
}
